import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Iconify from '@emfood/shared/src/components/iconify';
import { RHFSelect, FormProvider, RHFTextField } from '@emfood/shared';

import { LoadingButton } from '@mui/lab';
import {
  Stack,
  Button,
  MenuItem,
  Typography,
  DialogTitle,
  DialogActions,
  DialogContent,
} from '@mui/material';

import { RHFUpload } from 'src/components/hook-form/rhf-upload';
import { useCreateOrderIssue, useUploadOrderIssueImage } from 'src/api/hooks/api-hooks';
import { ProductIssueType, ProductIssueResolutionType } from 'src/api/models/orders';

type ReportProductIssueFormProps = {
  orderId: string;
  productId: string;
  productName: string;
  productUnits: string;
  issueType: {
    name: string;
    description: string;
    id: ProductIssueType;
  };
  goBack: (isDirty: boolean) => void;
  onSuccess: () => void;
  onCancel: (isDirty: boolean) => void;
};

const MAX_PHOTO_SIZE_BYTES = 5 * 1024 * 1024;

const validatePhotoSize = (file: File) =>
  file.size > MAX_PHOTO_SIZE_BYTES
    ? { code: 'file-too-large', message: 'This photo is larger than 5MB' }
    : null;

const ReportProductIssueSchema = Yup.object().shape({
  details: Yup.string().label('More Information').required(),
  resolutionType: Yup.string().label('Resolution Type').required(),
  photos: Yup.array().of(Yup.mixed<File>().required()).max(5, 'You can attach up to 5 photos'),
});

const ReportProductIssueForm = ({
  orderId,
  productId,
  productName,
  productUnits,
  issueType,
  goBack,
  onSuccess,
  onCancel,
}: ReportProductIssueFormProps) => {
  const defaultValues = {
    details: '',
    resolutionType: '',
    photos: [] as File[],
  };

  const { mutateAsync: createOrderIssue } = useCreateOrderIssue();
  const { mutateAsync: uploadOrderIssueImage } = useUploadOrderIssueImage();

  const methods = useForm({
    resolver: yupResolver(ReportProductIssueSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isDirty },
    reset,
  } = methods;

  const { enqueueSnackbar } = useSnackbar();

  const handleFormSubmit = handleSubmit(async (data) => {
    if (issueType) {
      try {
        const imageUrls = await Promise.all(
          (data.photos ?? []).map(async (file) => {
            const fileFormData = new FormData();
            fileFormData.append('file-data', file);
            const uploadResult = await uploadOrderIssueImage({
              body: fileFormData,
              pathParams: { orderId },
            });
            return uploadResult.result;
          })
        );

        const submitData = {
          buyerOrderProductId: productId,
          issueType: issueType.id,
          details: data.details,
          resolutionType: Number(data.resolutionType),
          images: imageUrls,
        };
        await createOrderIssue({ body: submitData, pathParams: { id: orderId } });

        onSuccess();
        reset();
      } catch {
        enqueueSnackbar('Your issue was not submitted. Please try again.', { variant: 'error' });
      }
    }
  });

  // const getResolutionOptions = () => {
  //   switch (issueType) {
  //     case 'missing':
  //     case 'quality':
  //     case 'incorrect':
  //     case 'shelfLife':
  //       return (
  //         <>
  //           <MenuItem value="refund">Refund</MenuItem>
  //           <MenuItem value="replacement">Replace items</MenuItem>
  //         </>
  //       );
  //     case 'note':
  //       return <MenuItem value="other">Reissue document</MenuItem>;
  //     default:
  //       return null;
  //   }
  // };

  return (
    <FormProvider methods={methods} onSubmit={handleFormSubmit}>
      <DialogTitle>
        <Button
          variant="outlined"
          color="inherit"
          onClick={() => goBack(isDirty)}
          startIcon={<Iconify icon="mdi:chevron-left" />}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
        <div>
          {issueType && issueType.name}
          <Typography variant="subtitle1">{issueType.description}</Typography>
        </div>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1">{productName}</Typography>
        <Typography variant="body1" color="GrayText" sx={{ mb: 3 }}>
          {productUnits}
        </Typography>
        <Stack sx={{ mt: 2 }} spacing={2}>
          <RHFTextField
            name="details"
            label="Please add detail to explain your issue"
            fullWidth
            multiline
            placeholder="Write in your own words a summary of the issue"
            minRows={3}
          />
          <RHFSelect
            name="resolutionType"
            label="What resolution would you like to receive?"
            fullWidth
          >
            <MenuItem value={ProductIssueResolutionType.Refund}>Refund</MenuItem>
            <MenuItem value={ProductIssueResolutionType.Replace}>Replace</MenuItem>
            <MenuItem value={ProductIssueResolutionType['No Action']}>No action</MenuItem>
          </RHFSelect>
          <Stack spacing={1}>
            <Typography variant="subtitle2">Add photos (optional)</Typography>
            <RHFUpload
              name="photos"
              multiple
              thumbnail
              accept={{ 'image/*': ['.jpeg', '.jpg', '.png'] }}
              validator={validatePhotoSize}
              helperText="Up to 5 photos, 5MB each. Include a photo of the product label showing the best-before date and batch number — the supplier needs this to investigate."
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onCancel(isDirty)}>Cancel</Button>
        {issueType && (
          <LoadingButton
            loading={isSubmitting}
            onClick={handleFormSubmit}
            variant="contained"
            disabled={isSubmitting || !isDirty}
          >
            Save Changes
          </LoadingButton>
        )}
      </DialogActions>
    </FormProvider>
  );
};

export default ReportProductIssueForm;
